// Tiered asset preload manifest. The Preloader fires each tier in order at
// `startAll()`, parallelising within a tier. Only tier 1 blocks the
// LanguageSelectScreen → SplashScreen handoff; tiers 2-5 keep loading in the
// background through home / menus / vote / match.
//
// The manifest URLs are *just* paths — the Preloader warms the browser HTTP
// cache via fetch(), so the existing loaders (THREE.AudioLoader, GLTFLoader,
// <img>) hit the cache on second access without any code changes downstream.

export type AssetKind = 'audio' | 'gltf' | 'image';

export interface AssetEntry {
  url: string;
  kind: AssetKind;
  tier: 1 | 2 | 3 | 4 | 5;
}

export const ASSET_MANIFEST: ReadonlyArray<AssetEntry> = [
  // ─── Tier 1: tutorial ─────────────────────────────────────────────────
  // Tutorial is the first thing a fresh player sees, so its full asset set
  // loads ahead of everything else (including splash). Materials, env,
  // narration, music, and SFX all land in cache before we even paint the
  // splash screen.
  { url: '/materials/tutorial_env.ktx2',                                 kind: 'image', tier: 1 },
  { url: '/materials/tutorialdoor/material_basecolor.ktx2',              kind: 'image', tier: 1 },
  { url: '/materials/tutorialdoor/material_height.ktx2',                 kind: 'image', tier: 1 },
  { url: '/materials/tutorialdoor/material_metalness.ktx2',              kind: 'image', tier: 1 },
  { url: '/materials/tutorialdoor/material_normal.ktx2',                 kind: 'image', tier: 1 },
  { url: '/materials/tutorialdoor/material_roughness.ktx2',              kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_basecolor_blue.ktx2',     kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_basecolor_green.ktx2',    kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_basecolor_lightblue.ktx2',kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_basecolor_orange.ktx2',   kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_basecolor_purple.ktx2',   kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_basecolor_red.ktx2',      kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_normal.ktx2',             kind: 'image', tier: 1 },
  { url: '/materials/tutorialsurfaces/material_roughness.ktx2',          kind: 'image', tier: 1 },
  { url: '/materials/treadmillfloor/material_basecolor.ktx2',            kind: 'image', tier: 1 },
  { url: '/materials/treadmillfloor/material_height.ktx2',               kind: 'image', tier: 1 },
  { url: '/materials/treadmillfloor/material_normal.ktx2',               kind: 'image', tier: 1 },
  { url: '/materials/treadmillfloor/material_roughness.ktx2',            kind: 'image', tier: 1 },
  { url: '/materials/treadmillfloor/material_texture.ktx2',              kind: 'image', tier: 1 },
  { url: '/sounds/music-tutorial.ogg',                                   kind: 'audio', tier: 1 },
  { url: '/sounds/irs/tutorial.ogg',                                     kind: 'audio', tier: 1 },
  { url: '/sounds/tutorial/door_open.ogg',                               kind: 'audio', tier: 1 },
  { url: '/sounds/tutorial/treadmill_loop.ogg',                          kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/0_1_bored.ogg',                  kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/0_2_bored.ogg',                  kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/0_3_bored.ogg',                  kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/1_1_asleep.ogg',                 kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/1_2_awake.ogg',                  kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/2_1_sprint.ogg',                 kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/3_1_run_jump.ogg',               kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/4_1_wall_climb.ogg',             kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/5_1_jimmy_combat.ogg',           kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/6_1_2_careful.ogg',              kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/6_1_throw_jimmy.ogg',            kind: 'audio', tier: 1 },
  { url: '/sounds/narration/tutorial/en/6_2_follow_jimmy.ogg',           kind: 'audio', tier: 1 },

  // ─── Tier 2: splash-critical ──────────────────────────────────────────
  // The perfectly-timed splash choreography (logo slam at 2.25s, music cue
  // at 1.0s) needs these in cache before splash starts.
  { url: '/sounds/music-home.ogg',            kind: 'audio', tier: 2 },
  { url: '/sounds/splash-to-home-whoosh.ogg', kind: 'audio', tier: 2 },
  { url: '/ui/floppy-splash-logo.svg',        kind: 'image', tier: 2 },

  // ─── Tier 3: home / menus / changing room ─────────────────────────────
  { url: '/sounds/ui-hover.ogg',                   kind: 'audio', tier: 3 },
  { url: '/sounds/ui-click.ogg',                   kind: 'audio', tier: 3 },
  { url: '/sounds/ui-changing-room-sweep.ogg',     kind: 'audio', tier: 3 },
  { url: '/sounds/ui-map-screen-in-out-sweep.ogg', kind: 'audio', tier: 3 },
  // Hat GLBs — needed for the home preview's hat picker + the in-game
  // character's worn hat. 17 entries, fetched in parallel.
  { url: '/models/hats/top+hat.glb',           kind: 'gltf', tier: 3 },
  { url: '/models/hats/chef+hat.glb',          kind: 'gltf', tier: 3 },
  { url: '/models/hats/baseball+cap.glb',      kind: 'gltf', tier: 3 },
  { url: '/models/hats/beanie.glb',            kind: 'gltf', tier: 3 },
  { url: '/models/hats/bucket+hat.glb',        kind: 'gltf', tier: 3 },
  { url: '/models/hats/cowboy+hat.glb',        kind: 'gltf', tier: 3 },
  { url: '/models/hats/firefighter+helmet.glb',kind: 'gltf', tier: 3 },
  { url: '/models/hats/gold+crown.glb',        kind: 'gltf', tier: 3 },
  { url: '/models/hats/graduation+cap.glb',    kind: 'gltf', tier: 3 },
  { url: '/models/hats/halo+ring.glb',         kind: 'gltf', tier: 3 },
  { url: '/models/hats/party+hat.glb',         kind: 'gltf', tier: 3 },
  { url: '/models/hats/pirate+hat.glb',        kind: 'gltf', tier: 3 },
  { url: '/models/hats/santa+hat.glb',         kind: 'gltf', tier: 3 },
  { url: '/models/hats/sombrero.glb',          kind: 'gltf', tier: 3 },
  { url: '/models/hats/viking+helmet.glb',     kind: 'gltf', tier: 3 },
  { url: '/models/hats/wizard+hat.glb',        kind: 'gltf', tier: 3 },
  // Map picker thumbnails + shared backgrounds + UI imagery
  { url: '/ui/map-selection-bg.webp',           kind: 'image', tier: 3 },
  { url: '/ui/map-thumbnail-sun.webp',          kind: 'image', tier: 3 },
  { url: '/ui/map-thumbnail-moon.webp',         kind: 'image', tier: 3 },
  { url: '/ui/map-thumbnail-pirate.webp',       kind: 'image', tier: 3 },
  { url: '/ui/map-thumbnail-saturn.webp',       kind: 'image', tier: 3 },
  { url: '/ui/map-thumbnail-airship.webp',      kind: 'image', tier: 3 },
  { url: '/ui/game-crown.webp',                 kind: 'image', tier: 3 },
  { url: '/ui/wins-icon.webp',                  kind: 'image', tier: 3 },
  { url: '/ui/earth-icon.webp',                 kind: 'image', tier: 3 },
  { url: '/ui/dice-icon.svg',                  kind: 'image', tier: 3 },
  { url: '/ui/play-icon.svg',                  kind: 'image', tier: 3 },
  { url: '/ui/small-check-circle.svg',         kind: 'image', tier: 3 },
  { url: '/ui/large-check-circle.svg',         kind: 'image', tier: 3 },
  { url: '/ui/onlineplay/find-match-icon.svg', kind: 'image', tier: 3 },
  { url: '/ui/onlineplay/leave-party-icon.svg',kind: 'image', tier: 3 },
  { url: '/ui/onlineplay/join-party.svg',      kind: 'image', tier: 3 },
  { url: '/ui/onlineplay/copy-join-code-icon.svg', kind: 'image', tier: 3 },
  { url: '/ui/onlineplay/party-owner-circle.svg',  kind: 'image', tier: 3 },
  { url: '/ui/onlineplay/party-owner-circle.webp',  kind: 'image', tier: 3 },

  // ─── Tier 4: shared in-match SFX (every map uses these) ───────────────
  { url: '/sounds/hit.ogg',                    kind: 'audio', tier: 4 },
  { url: '/sounds/swing.ogg',                  kind: 'audio', tier: 4 },
  { url: '/sounds/swing-suno.ogg',             kind: 'audio', tier: 4 },
  { url: '/sounds/round-crown.ogg',            kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-001.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-002.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-003.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-004.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-005.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-006.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-007.ogg',     kind: 'audio', tier: 4 },
  { url: '/sounds/footstep-metal-008.ogg',     kind: 'audio', tier: 4 },

  // ─── Tier 5: per-map heavy assets + uncurated bucket ─────────────────
  // Curated map-specific GLBs / music / textures plus anything
  // auto-appended by asset-compact that didn't already have a manifest
  // entry. Whichever map plays first wins the race; the others stay
  // warm in cache for play-again / next-match cases.
  // Spaceship in Saturn's Rings
  { url: '/models/world/spaceship.glb',        kind: 'gltf', tier: 5 },
  { url: '/sounds/music-saturn.ogg',           kind: 'audio', tier: 5 },
  { url: '/sounds/thruster.ogg',               kind: 'audio', tier: 5 },
  // Sinking Ship (pirate map)
  { url: '/models/world/wooden-boat.glb',      kind: 'gltf', tier: 5 },
  { url: '/sounds/pirate/music-pirate.ogg',    kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/ocean.ogg',           kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/shipsinking.ogg',     kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/shipcreakloop.ogg',   kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/footstep-water-001.ogg', kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/footstep-water-002.ogg', kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/footstep-water-003.ogg', kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/footstep-wood-001.ogg',  kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/footstep-wood-002.ogg',  kind: 'audio', tier: 5 },
  { url: '/sounds/pirate/footstep-wood-003.ogg',  kind: 'audio', tier: 5 },
  // Sketchy Airship
  { url: '/sounds/airship/ambient-engine.ogg',       kind: 'audio', tier: 5 },
  { url: '/sounds/airship/ambient-outside-wind.ogg', kind: 'audio', tier: 5 },
  // Material textures referenced by current map bundles
  { url: '/materials/water/waternormals.ktx2',                    kind: 'image', tier: 5 },
  { url: '/materials/metalstuds/metalstuds_basecolor.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/metalstuds/metalstuds_normal.ktx2',          kind: 'image', tier: 5 },
  { url: '/materials/metalstuds/metalstuds_roughness.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/metalstuds/metalstuds_metalness.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/metalstuds/metalstuds_heightmap.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/woodenbox/material_basecolor.ktx2',          kind: 'image', tier: 5 },
  { url: '/materials/woodenbox/material_normal.ktx2',             kind: 'image', tier: 5 },
  { url: '/materials/woodenbox/material_roughness.ktx2',          kind: 'image', tier: 5 },
  { url: '/materials/woodenbox/material_height.ktx2',             kind: 'image', tier: 5 },
  { url: '/materials/sideplatform/material_basecolor.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/sideplatform/material_normal.ktx2',          kind: 'image', tier: 5 },
  { url: '/materials/sideplatform/material_roughness.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/sideplatform/material_metalness.ktx2',       kind: 'image', tier: 5 },
  { url: '/materials/sideplatform/material_height.ktx2',          kind: 'image', tier: 5 },
  { url: '/materials/airship_env.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/airshipwall/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/airshipwall/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/airshipwall/material_metalness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/airshipwall/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/airshipwall/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrel/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrel/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrel/material_metalness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrel/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrel/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrelalt/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrelalt/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrelalt/material_metalness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrelalt/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/barrelalt/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloor/canvas_basetexture.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloor/canvas_color.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloor/canvas_heightmap.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloor/canvas_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloor/canvas_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloorsticker/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloorsticker/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/boxingfloorsticker/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fan/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fan/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fan/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fan/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fanbacking/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fanbacking/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fanbacking/material_metalness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fanbacking/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/fanbacking/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moon_env_alt.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moon_env.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingcanvas/canvas_basetexture.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingcanvas/canvas_color.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingcanvas/canvas_heightmap.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingcanvas/canvas_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingcanvas/canvas_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_basecolor_blue.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_basecolor_red.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_texture_blue.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonboxingpillars/material_texture_red.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondome/DirtWindowStains005_ALPHAMASKED_2K.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondome/DirtWindowStains005_COL_2K.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondome/DirtWindowStains005_GLOSS_2K.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondome/DirtWindowStains005_NRM_2K.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondome/DirtWindowStains005_REFL_2K.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomeframe/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomeframe/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomeframe/material_metalness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomeframe/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomeframe/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomeframe/material_texture.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_AmbientOcclusion.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_BaseColor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_Displacement.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_Metallic.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_Normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_Roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moondomev2/Poliigon_GlassDecorative_10261_Transmission.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonsurface/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonsurface/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonsurface/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonsurface/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/moonsurface/material_texture.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/pirateship_env.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/rope/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/rope/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/rope/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/rope/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturn_env_one.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturn_env_three.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturn_env_two.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturnmeteors/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturnmeteors/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturnmeteors/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/saturnmeteors/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_basecolor.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_height.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_metalness_full.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_metalness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_normal.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_roughness_notrough.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_roughness.ktx2', kind: 'image', tier: 5 },
  { url: '/materials/submarine/material_texture.ktx2', kind: 'image', tier: 5 },
  { url: '/models/hats/chef+hat+old.glb', kind: 'gltf', tier: 5 },
  { url: '/models/hats/top+hat+old.glb', kind: 'gltf', tier: 5 },
  { url: '/models/world/submarine-old.glb', kind: 'gltf', tier: 5 },
  { url: '/models/world/submarine.glb', kind: 'gltf', tier: 5 },
  { url: '/models/world/wooden-boat-old.glb', kind: 'gltf', tier: 5 },
  { url: '/sounds/ESM_Bubble_Pop_Shoot_v2_Game_Organic_Cartoon.ogg', kind: 'audio', tier: 5 },
  { url: '/sounds/ESM_VCSFX_FX_foley_one_shot_rhythmic_block_tap_steady_pulse_01.ogg', kind: 'audio', tier: 5 },
  { url: '/sounds/irs/moon.ogg', kind: 'audio', tier: 5 },
  { url: '/sounds/music-moon.ogg', kind: 'audio', tier: 5 }
];
